import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MessageCircle, X, Phone, Send, Clock } from "lucide-react";
import { SITE } from "@/lib/site";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CRENEAUX = [
  "Dès que possible",
  "Dans l'heure",
  "Aujourd'hui — matin",
  "Aujourd'hui — après-midi",
  "Demain",
  "En soirée",
];

export default function CallbackWidget() {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0); // 0=intro, 1=form, 2=sent
  const [form, setForm] = useState({ nom: "", telephone: "", creneau: CRENEAUX[0], message: "" });
  const [loading, setLoading] = useState(false);
  const [hasNudge, setHasNudge] = useState(false);

  // Show a subtle nudge after 8s if user hasn't opened the widget
  useEffect(() => {
    const t = setTimeout(() => setHasNudge(true), 8000);
    return () => clearTimeout(t);
  }, []);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.nom.trim() || !form.telephone.trim()) {
      toast.error("Merci d'indiquer votre nom et téléphone.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/callback`, form);
      setStep(2);
      toast.success("Demande envoyée. Nous vous rappelons rapidement.");
    } catch (err) {
      console.error(err);
      toast.error("Une erreur est survenue. Merci de nous appeler directement.");
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setStep(0);
    setForm({ nom: "", telephone: "", creneau: CRENEAUX[0], message: "" });
  };

  return (
    <>
      {/* FLOATING TRIGGER BUTTON */}
      {!open && (
        <button
          data-testid="callback-widget-open"
          onClick={() => { setOpen(true); setHasNudge(false); }}
          aria-label="Demander à être rappelé"
          className="fixed z-40 right-4 bottom-24 md:bottom-6 group flex items-center gap-3 bg-black text-white pl-4 pr-5 py-3 rounded-full shadow-lg hover:shadow-xl hover:bg-neutral-800 transition-all duration-500 hover:-translate-y-0.5"
        >
          <span className="relative flex h-9 w-9 items-center justify-center rounded-full bg-white text-black">
            <MessageCircle size={18} strokeWidth={1.5} />
            {hasNudge && (
              <span className="absolute -top-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-white ring-2 ring-black">
                <span className="absolute inline-flex h-full w-full rounded-full bg-white opacity-75 animate-ping" />
              </span>
            )}
          </span>
          <span className="hidden sm:inline text-sm tracking-wide whitespace-nowrap">
            Être rappelé
          </span>
        </button>
      )}

      {/* PANEL */}
      {open && (
        <div
          data-testid="callback-widget-panel"
          className="fixed z-50 right-4 bottom-24 md:bottom-6 w-[92vw] max-w-sm bg-white border border-black/15 shadow-2xl animate-in slide-in-from-bottom-4 duration-300"
        >
          {/* HEADER */}
          <div className="bg-black text-white px-5 py-4 flex items-center justify-between">
            <div>
              <p className="text-xs uppercase tracking-[0.2em] opacity-60">Pompes Funèbres</p>
              <p className="font-serif-display text-xl leading-tight">Lumière des Nations</p>
            </div>
            <button
              data-testid="callback-widget-close"
              onClick={() => { setOpen(false); if (step === 2) reset(); }}
              aria-label="Fermer"
              className="p-1 hover:opacity-70 transition-opacity"
            >
              <X size={20} strokeWidth={1.5} />
            </button>
          </div>

          {/* BODY */}
          <div className="p-5 max-h-[70vh] overflow-y-auto">
            {step === 0 && (
              <>
                <div className="flex gap-3">
                  <div className="w-8 h-8 rounded-full bg-black text-white flex items-center justify-center flex-none">
                    <MessageCircle size={14} strokeWidth={1.5} />
                  </div>
                  <div className="bg-neutral-100 px-4 py-3 rounded-sm">
                    <p className="font-serif-display text-lg leading-snug">
                      Bonjour, nous sommes là pour vous.
                    </p>
                    <p className="mt-2 text-sm text-neutral-700 leading-relaxed">
                      Souhaitez-vous que nous vous rappelions ? Laissez-nous votre numéro,
                      nous vous répondrons dans les plus brefs délais.
                    </p>
                  </div>
                </div>

                <div className="mt-6 flex flex-col gap-2">
                  <button
                    data-testid="callback-widget-start"
                    onClick={() => setStep(1)}
                    className="w-full inline-flex items-center justify-center gap-2 bg-black text-white py-3 rounded-sm text-sm tracking-wide hover:bg-neutral-800 transition-colors"
                  >
                    <Phone size={16} strokeWidth={1.5} /> Demander un rappel
                  </button>
                  <a
                    href={SITE.phoneHref}
                    data-testid="callback-widget-direct-call"
                    className="w-full inline-flex items-center justify-center gap-2 border border-black py-3 rounded-sm text-sm tracking-wide hover:bg-black hover:text-white transition-colors"
                  >
                    Appeler maintenant — {SITE.phone}
                  </a>
                </div>

                <p className="mt-6 flex items-center justify-center gap-2 text-xs text-neutral-500">
                  <Clock size={12} strokeWidth={1.5} />
                  {SITE.availability}
                </p>
              </>
            )}

            {step === 1 && (
              <form onSubmit={submit} data-testid="callback-widget-form" className="space-y-4">
                <p className="font-serif-display text-lg leading-tight">
                  Laissez-nous votre numéro, on s'occupe du reste.
                </p>

                <div>
                  <label htmlFor="cb-nom" className="text-xs uppercase tracking-[0.2em] text-neutral-500">
                    Votre nom *
                  </label>
                  <input
                    id="cb-nom"
                    data-testid="callback-input-nom"
                    type="text"
                    required
                    value={form.nom}
                    onChange={update("nom")}
                    className="mt-2 w-full border-b border-black/30 focus:border-black bg-transparent py-2 text-sm outline-none transition-colors"
                  />
                </div>
                <div>
                  <label htmlFor="cb-tel" className="text-xs uppercase tracking-[0.2em] text-neutral-500">
                    Téléphone *
                  </label>
                  <input
                    id="cb-tel"
                    data-testid="callback-input-tel"
                    type="tel"
                    required
                    inputMode="tel"
                    placeholder="06 __ __ __ __"
                    value={form.telephone}
                    onChange={update("telephone")}
                    className="mt-2 w-full border-b border-black/30 focus:border-black bg-transparent py-2 text-sm outline-none transition-colors"
                  />
                </div>
                <div>
                  <label htmlFor="cb-creneau" className="text-xs uppercase tracking-[0.2em] text-neutral-500">
                    Quand vous rappeler ?
                  </label>
                  <select
                    id="cb-creneau"
                    data-testid="callback-input-creneau"
                    value={form.creneau}
                    onChange={update("creneau")}
                    className="mt-2 w-full border-b border-black/30 focus:border-black bg-transparent py-2 text-sm outline-none transition-colors"
                  >
                    {CRENEAUX.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label htmlFor="cb-msg" className="text-xs uppercase tracking-[0.2em] text-neutral-500">
                    Un mot (facultatif)
                  </label>
                  <textarea
                    id="cb-msg"
                    data-testid="callback-input-message"
                    rows={2}
                    value={form.message}
                    onChange={update("message")}
                    className="mt-2 w-full border border-black/15 focus:border-black bg-transparent p-2 text-sm outline-none transition-colors resize-none"
                  />
                </div>

                <button
                  type="submit"
                  data-testid="callback-widget-submit"
                  disabled={loading}
                  className="w-full inline-flex items-center justify-center gap-2 bg-black text-white py-3 rounded-sm text-sm tracking-wide hover:bg-neutral-800 transition-colors disabled:opacity-60 disabled:cursor-wait"
                >
                  {loading ? "Envoi…" : (<><Send size={14} strokeWidth={1.5} /> Envoyer ma demande</>)}
                </button>
                <p className="text-[11px] text-neutral-500 leading-snug">
                  Vos données sont confidentielles et servent uniquement à vous rappeler.
                </p>
              </form>
            )}

            {step === 2 && (
              <div data-testid="callback-widget-success" className="text-center py-6">
                <div className="w-14 h-14 rounded-full bg-black text-white flex items-center justify-center mx-auto">
                  <Phone size={22} strokeWidth={1.25} />
                </div>
                <p className="font-serif-display text-2xl mt-6 leading-tight">
                  Merci {form.nom.split(" ")[0]}.
                </p>
                <p className="mt-3 text-sm text-neutral-700 leading-relaxed">
                  Nous vous rappelons au <strong className="font-medium">{form.telephone}</strong>{" "}
                  ({form.creneau.toLowerCase()}).
                </p>
                <button
                  onClick={() => { setOpen(false); reset(); }}
                  data-testid="callback-widget-done"
                  className="mt-8 inline-flex items-center justify-center gap-2 border border-black px-6 py-3 rounded-sm text-sm hover:bg-black hover:text-white transition-colors"
                >
                  Fermer
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
